/**
 * Header Component
 * Main navigation header
 */

import { Link, useNavigate } from 'react-router-dom';
import { useCounties } from '../../hooks/useCounties';
import { useAuth } from '../../contexts/AuthContext';
import { SolutionsDropdown } from '../navigation/SolutionsDropdown';
import { UserMenu } from '../navigation/UserMenu';

export function Header() {
  const { counties } = useCounties();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="bg-white border-b border-neutral-200">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
        <div className="flex items-center h-16">
          {/* Left Section: Brand + Primary Navigation */}
          <div className="flex items-center">
            {/* Brand Group */}
            <div className="flex items-center gap-3 mr-10">
              <Link to="/" className="text-2xl font-bold text-primary-600">
                CalWMP
              </Link>
              <span className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-md border border-green-200">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                CalGreen 5.408 Compliant
              </span>
            </div>

            {/* Primary Navigation - Visually connected to brand */}
            <div className="hidden md:flex items-center gap-8">
              <SolutionsDropdown />

              <div className="relative group">
                <button className="text-neutral-700 hover:text-neutral-900 transition-colors flex items-center gap-1 py-2">
                  Counties
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute left-0 pt-2 w-48 hidden group-hover:block z-50">
                  <div className="bg-white rounded-md shadow-lg py-1 border border-neutral-200">
                    {counties.map((county) => (
                      <Link
                        key={county.id}
                        to={`/counties/${county.id}`}
                        className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                      >
                        {county.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {isAuthenticated && (
                <>
                  <Link
                    to="/dashboard"
                    className="text-neutral-700 hover:text-neutral-900 transition-colors"
                  >
                    My Plans
                  </Link>
                  <Link
                    to="/my-projects"
                    className="text-neutral-700 hover:text-neutral-900 transition-colors"
                  >
                    My Projects
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Right Section: User Actions - Pushed to the right */}
          <div className="ml-auto flex items-center gap-4">
            {isAuthenticated && user ? (
              <UserMenu userEmail={user.email} onLogout={handleLogout} />
            ) : (
              <Link to="/plans/create" className="btn-primary">
                Get Started
              </Link>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
}